import { ActionType, UpdateApiVersionAction, UpdatePreferencesAction } from '@/actions/actiontypes';
import { statePreferences, stateSplash } from '@/selectors/selectors';
import { API_VERSION_STORAGE_KEY, ApiVersion, PreferencesState } from '@/state';
import { SplashState } from '@/state/SplashState';
import { useDispatch, useSelector } from '@/state/store';
import { SVVButton } from '@komponentkassen/svv-button';
import { SVVModal } from '@komponentkassen/svv-modal';
import { useState } from 'react';
import SimpleSelect from '../objects/SimpleSelect';
import SimpleToggle from '../objects/SimpleToggle';
import { runtimeEnvironment } from '@/config';
import { getApiVersion } from '@/utils/Utils';
import { apiUpdateAction } from '@/actions/metaActions';

interface PreferencesDialogProps {
    handleWindowChange: (window: SplashState) => void;
}

interface ToggleProps {
    name: string;
    label: string;
}

interface SelectProps extends ToggleProps {
    options: string[];
    defaultValue?: string;
}

export const PreferencesDialog = ({ handleWindowChange }: PreferencesDialogProps) => {
    const preferences = useSelector(statePreferences);
    const modalState = useSelector(stateSplash);
    const dispatch = useDispatch();

    const [selectedPreferences, setSelectedPreferences] = useState({ ...preferences });
    const [showWarning, setShowWarning] = useState(false);
    const [selectedApiVersion, setSelectedApiVersion] = useState(getApiVersion());

    const handleToggle = (name: string) => {
        setSelectedPreferences({
            ...selectedPreferences,
            [name]: !selectedPreferences[name],
        });
    };

    const handleSelect = (name: string, event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedApiVersion(event.target.value as ApiVersion);
    };

    const handleClose = () => {
        if (
            (JSON.stringify(preferences) !== JSON.stringify(selectedPreferences) ||
                getApiVersion() !== selectedApiVersion) &&
            !showWarning
        ) {
            setShowWarning(true);
        } else {
            setShowWarning(false);
            handleWindowChange(SplashState.NONE);
        }
    };

    const handleSubmit = () => {
        const action: UpdatePreferencesAction = {
            type: ActionType.PREFERENCES_UPDATE,
            ...selectedPreferences,
        };
        dispatch(action);
        handleWindowChange(SplashState.NONE);

        if (getApiVersion() !== selectedApiVersion) {
            sessionStorage.setItem(API_VERSION_STORAGE_KEY, selectedApiVersion);
            dispatch(apiUpdateAction());
        }
    };

    const handleReset = () => {
        setSelectedPreferences(new PreferencesState());
    };

    const WarningModal = () => (
        <SVVModal
            show={showWarning}
            title="Advarsel!"
            onClose={() => {
                setShowWarning(!showWarning);
            }}
            rejectButton={
                <SVVButton color="secondary" onClick={() => setShowWarning(!showWarning)}>
                    Nei
                </SVVButton>
            }
            acceptButton={<SVVButton onClick={() => handleClose()}>Ja</SVVButton>}
        >
            <div className="c-preferences-toggle-content">
                Du har ulagrede endringer, er du sikker på at du vil fortsette?
            </div>
        </SVVModal>
    );

    return (
        <>
            <WarningModal />
            <SVVModal
                show={modalState === SplashState.PREFERENCES && !showWarning}
                onClose={() => handleClose()}
                title="Innstillinger"
                rejectButton={
                    <SVVButton onClick={() => handleReset()} color="secondary">
                        Nullstill innstillinger
                    </SVVButton>
                }
                acceptButton={<SVVButton onClick={() => handleSubmit()}>Lagre innstillinger</SVVButton>}
                disableCloseOnRejectClick
            >
                <div className="c-preferences-toggle-content">
                    <h2 className="c-preferences-heading">Vegkart</h2>
                    <SimpleToggle
                        className="c-preferences-toggle"
                        checked={selectedPreferences['includeTrafikantgruppe'] as boolean}
                        label={'Vis trafikantgruppe i vegsystemreferanse'}
                        onChange={() => handleToggle('includeTrafikantgruppe')}
                    />

                    <SimpleToggle
                        className="c-preferences-toggle"
                        checked={selectedPreferences['transformTinyGeometries'] as boolean}
                        label={'Vis små strekningsgeometrier som punkt'}
                        onChange={() => handleToggle('transformTinyGeometries')}
                    />
                    <SimpleToggle
                        className="c-preferences-toggle"
                        checked={selectedPreferences['disableClusters'] as boolean}
                        label={'Vis punktgruppe som separate punkter'}
                        onChange={() => handleToggle('disableClusters')}
                    />
                    <SimpleToggle
                        className="c-preferences-toggle"
                        checked={selectedPreferences['showQualityInfo'] as boolean}
                        label={'Vis kvalitetsinformasjon for valgt objekt'}
                        onChange={() => handleToggle('showQualityInfo')}
                    />
                    <SimpleToggle
                        className="c-preferences-toggle"
                        checked={selectedPreferences['fetchStatisticsLength'] as boolean}
                        label={'Vis veglengder i statistikk'}
                        onChange={() => handleToggle('fetchStatisticsLength')}
                    />
                    <SimpleToggle
                        className="c-preferences-toggle"
                        checked={selectedPreferences['showMetadata'] as boolean}
                        label={'Vis metadata for valgt objekt'}
                        onChange={() => handleToggle('showMetadata')}
                    />
                    <SimpleToggle
                        className="c-preferences-toggle"
                        checked={selectedPreferences['showPropertyBoundaries'] as boolean}
                        label={'Vis eiendomsgrenser'}
                        onChange={() => handleToggle('showPropertyBoundaries')}
                    />
                </div>
                <div className="c-preferences-toggle-content">
                    <h2 className="c-preferences-heading">API-versjon</h2>
                    <SimpleSelect
                        label="API-versjon (kun relevant for testing)"
                        options={[ApiVersion.V3, ApiVersion.V4]}
                        value={selectedApiVersion}
                        onChange={e => handleSelect(name, e)}
                    />
                </div>
                {selectedApiVersion === ApiVersion.V4 && runtimeEnvironment() === 'prod' && (
                    <div>
                        <p className="c-preferences-warning-text">
                            API-v4 er under utvikling, og åpnes for bruk slik at Vegkart-brukere kan teste det
                            som kommer. Faktisk bruk vil gi systemutviklerne nyttige erfaringer.
                        </p>
                        <p className="c-preferences-warning-text">
                            <b>NB! Bruk fremdeles API-v3 til viktige oppgaver.</b>
                        </p>
                        <p className="c-preferences-warning-text">
                            Valg av API endrer ikke på noen funksjoner i Vegkart. Se mer her:
                        </p>
                        <a href="https://nvdb.atlas.vegvesen.no/nyheter/2024/11/05/">
                            https://nvdb.atlas.vegvesen.no/nyheter/2024/11/05/
                        </a>
                    </div>
                )}
                {selectedApiVersion === ApiVersion.V4 && runtimeEnvironment() !== 'prod' && (
                    <div>
                        <p className="c-preferences-warning-text">
                            API-v4 er i akseptansetest. Følg med for oppdatert informasjon:
                        </p>
                        <a href="https://nvdb.atlas.vegvesen.no/nyheter/">
                            https://nvdb.atlas.vegvesen.no/nyheter/
                        </a>
                    </div>
                )}
            </SVVModal>
        </>
    );
};

PreferencesDialog.displayName = 'PreferencesDialog';
export default PreferencesDialog;
